import { UtilitiesService } from './../../services/utilities/utilities.service';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Logger } from '@app/core/logger.service';
import { FirebaseService } from '@app/shared/services/firebase/firebase.service';
import { SpecialtiesService } from '@app/shared/services/specialties/specialties.service';
import { IonLoaderService } from '@app/shared/services/ion-loader/ion-loader.service';
import { ActionSheetController, AlertController, LoadingController, PopoverController } from '@ionic/angular';
import { untilDestroyed } from '@app/core/until-destroyed';
import { TooltipComponent } from '@app/register/tooltip/tooltip.component';
import { Camera, CameraResultType, Photo } from '@capacitor/camera';
import { CredentialsService } from '@app/core/authentication/credentials.service';
import { FilesService } from '@app/shared/services/files/files.service';
import { Router } from '@angular/router';
const folderName = 'register-files';
const log = new Logger('ProfessionalDataFormComponent');
export interface UserData {
  uid: string;
  email: string;
}
@Component({
  selector: 'app-professional-data-form',
  templateUrl: './professional-data-form.component.html',
  styleUrls: ['./professional-data-form.component.scss']
})
export class ProfessionalDataFormComponent implements OnDestroy, OnInit {
  @ViewChild('file', { read: ElementRef, static: true }) file: ElementRef;
  public dataFormEsp: FormGroup;
  @Input() user: UserData;
  @Input() button = 'Siguiente';
  @Output() response = new EventEmitter<void>();
  public dataForm: FormGroup;
  specialties: any[] = [];
  public isLoading = false;
  numberOfValidationRequests: number;

  constructor(
    private fb: FormBuilder,
    private loader: IonLoaderService,
    private fireService: FirebaseService,
    private specialtiesService: SpecialtiesService,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private actionSheetCtrl: ActionSheetController,
    private utilities: UtilitiesService,
    public credService: CredentialsService,
    private filesService: FilesService,
    private router: Router
  ) {
    this.initFormEsp();
  }


  async initHistory() {
    try {
      const { size } = await this.fireService.afs.collection(`users/${this.user.uid}/validation-requests`).ref.get();
      this.numberOfValidationRequests = size;
    } catch (error) {
      log.error(error);
    }
  }

  ngOnInit() { this.initialize(); }

  private initFormEsp() {
    this.dataFormEsp = this.fb.group({
      verificationFile: ['', [Validators.required]]
    });
  }

  async initialize() {
    this.loader.ionLoaderPresent();
    try {
      this.initForm();
      await this.loadSpecialties();
      await this.loadUserData();
      await this.initHistory();
    } catch (error) {
      log.error(error);
    }
    this.loader.ionLoaderDismiss();
  }

  async loadSpecialties() {
    try {
      this.specialties = await this.specialtiesService.getSpecialties();
    } catch (error) {
      log.error(error);
    }
  }

  initForm() {
    this.dataForm = this.fb.group({
      cedula: ['', Validators.required],
      specialty1: [''],
      professionalID1: [''],
      specialty2: [''],
      professionalID2: ['']
    });

    this.setupPairValidation('specialty1', 'professionalID1');
    this.setupPairValidation('specialty2', 'professionalID2');

    // VALIDACIÓN: especialidades distintas y cédulas distintas
    this.dataForm.valueChanges.pipe(untilDestroyed(this)).subscribe(val => {
      const s1 = val.specialty1;
      const s2 = val.specialty2;
      const c1 = val.professionalID1?.trim();
      const c2 = val.professionalID2?.trim();

      // Reset errores
      this.dataForm.get('specialty2').setErrors(null);
      this.dataForm.get('professionalID2').setErrors(null);

      if (s1 && s2 && s1 === s2) {
        this.dataForm.get('specialty2').setErrors({ sameSpecialty: true });
      }

      if (c1 && c2 && c1 === c2) {
        this.dataForm.get('professionalID2').setErrors({ sameCedula: true });
      }
    });
  }

  // Reutilizable: validación condicional por pares
  setupPairValidation(specialty: string, cedula: string) {
    const s = this.dataForm.get(specialty);
    const c = this.dataForm.get(cedula);

    s.valueChanges.pipe(untilDestroyed(this)).subscribe(v => {
      if (v || c.value) c.setValidators([Validators.required]);
      else c.clearValidators();
      c.updateValueAndValidity({ emitEvent: false });
    });

    c.valueChanges.pipe(untilDestroyed(this)).subscribe(v => {
      if (v || s.value) s.setValidators([Validators.required]);
      else s.clearValidators();
      s.updateValueAndValidity({ emitEvent: false });
    });
  }

  async loadUserData() {
    try {
      const data = await this.fireService.getDoctorData(this.user.uid);
      if (data) {
        this.dataForm.patchValue({
          cedula: data.cedula || '',
          specialty1: data.specialty1?.id || '',
          professionalID1: data.specialty1?.cedula || '',
          specialty2: data.specialty2?.id || '',
          professionalID2: data.specialty2?.cedula || '',
        });
      }
    } catch (error) {
      log.error(error);
    }
  }

  async submit() {
    const loader = await this.loadingCtrl.create({
      message: 'Registrando...',
      spinner: 'crescent'
    });
    await loader.present();

    try {
      const f = this.dataForm.value;
      await this.fireService.updateDoctor(this.user.uid, {
        cedula: f.cedula.trim().toLowerCase(),
        specialty1: { id: f.specialty1 || '', cedula: f.professionalID1.trim().toLowerCase() },
        specialty2: { id: f.specialty2 || '', cedula: f.professionalID2.trim().toLowerCase() }
      });

      this.response.emit();
    } catch (err) {
      log.error(err);
    }

    await loader.dismiss();
  }

  ngOnDestroy() {
    // Empty but required by untilDestroyed
  }

  async showTooltip(ev: any, text: string) {
    const popover = await this.popoverCtrl.create({
      component: TooltipComponent,
      componentProps: {
        text: text
      },
      event: ev,
      showBackdrop: false,
      cssClass: 'tooltip-popover'
    });

    await popover.present();
  }

  async presentActionSheet() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Seleccione una opción',
      buttons: [
        {
          text: 'Cámara',
          handler: () => {
            this.takePicture();
          }
        },
        {
          text: 'Documento',
          handler: () => {
            this.triggerClick1();
          }
        },
        {
          text: 'Cancelar',
          role: 'cancel',
          data: {
            action: 'cancel',
          },
        },
      ],
    });
    await actionSheet.present();
  }

  public async takePicture() {
    this.isLoading = true;
    await this.loader.ionLoaderPresent();
    try {
      const image: Photo = await Camera.getPhoto({
        quality: 100,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,

      });
      this.dataFormEsp.get('verificationFile').markAsTouched();
      this.dataFormEsp.get('verificationFile').patchValue(image);
      this.dataFormEsp.get('verificationFile').updateValueAndValidity();
    } catch (error) {
      log.error(error);
    }
    await this.loader.ionLoaderDismiss();
    this.isLoading = false;
  }

  triggerClick1() {
    try {
      this.file.nativeElement.click();
    } catch (error) {
      log.error(error);
    }
  }

  async selectFile(data: any) {
    this.isLoading = true;
    await this.loader.ionLoaderPresent();
    try {
      const file: FileList = data.files as FileList;
      this.dataFormEsp.get('verificationFile').markAsTouched();
      this.dataFormEsp.get('verificationFile').patchValue(file);
      this.dataFormEsp.get('verificationFile').updateValueAndValidity();
    } catch (error) {
      this.utilities.toast('Ha ocurrido un error. Inténtelo más tarde.', '¡Ups!');
      log.error(error);
    }
    await this.loader.ionLoaderDismiss();
    this.isLoading = false;
  }

  async uploadFile(file: any): Promise<string> {
    try {
      // folderName
      if (file.dataUrl) {
        const response: any = await this.filesService.uploadImageChatFromCamera(file, folderName);
        return response && response.main && response.main.url ? response.main.url : null;
      } else {
        const response: any = await this.filesService.selectAnyFile(file, folderName, ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']);
        return response && response.main && response.main.url ? response.main.url : null;
      }
    } catch (error) {
      log.error(error);
      return null
    }
  }

  async submitEsp() {
    // ⬅️ INICIO DEL LOADER
    const loader = await this.loadingCtrl.create({
      message: 'Enviando...',
      spinner: 'crescent'
    });
    await loader.present();
    // ⬅️ FIN: loader inicia antes de todo
    this.isLoading = true;
    this.disableForm(this.dataFormEsp, 'disable');
    try {
      const DATA: any = { ...this.dataFormEsp.value };
      const verificationFileUrl = await this.uploadFile(DATA.verificationFile);
      await this.fireService.afs.collection('users').doc(this.user.uid).update({ validatedStatus: 'pending', verificationFileUrl: verificationFileUrl, validatedFileRequestStatus: 'pending' });
      await this.dataFormEsp.get('verificationFile').setValue('');
      await this.dataFormEsp.get('verificationFile').updateValueAndValidity();
      await this.dataFormEsp.reset();
      const alert = await this.alertCtrl.create({
        header: '¡Correcto!',
        message: 'Su documento de validación ha sido enviado correctamente. En breve estaremos revisando su información.',
        buttons: ['Aceptar']
      });
      await this.router.navigate(['/home']);
      alert.onDidDismiss().then(() => {
        location.reload();
      });
      await alert.present();
    } catch (error) {
      log.error(error);
    }
    this.isLoading = false;
    // ⬅️ FINAL: cerrar loader pase lo que pase
    await loader.dismiss();
  }

  disableForm(form: FormGroup<any>, action: 'disable' | 'enable') {
    if (action === 'disable') {
      form.disable();
    } else {
      form.enable();
    }
  }
}